// ─────────────────────────────────────────────────────────────
// DENTAL OFFICE READY™ — all copy and links live here. Image URLs live in data/images.ts.
// ─────────────────────────────────────────────────────────────

export const CHECKOUT_URL = 'https://dental-office-ready.impultienda.ar/checkout';
/** Meta pixel: pixel dental office ready */
export const META_PIXEL_ID = '1097362583009175';

export const PAGE_TITLE = 'Dental Office Ready™ | Your Office Paperwork, Already Written';
export const META_DESCRIPTION =
  '10 ready-to-use treatment packs for dental offices: consent forms, chairside guides and patient instructions, already written.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'DENTAL OFFICE READY™';
/** Label for every button that scrolls to the offer section */
export const ctaLabel = 'GET MY OFFICE READY';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'GET MY OFFICE READY · $27';
export const price = '$27';

// ── 1. TOP BAR ───────────────────────────────────────────────
export const topBar = { lookInside: 'Look inside' };

// ── 2. HERO (title, short subtitle, mockup, button — nothing else) ──
export const hero = {
  eyebrow: 'FOR DENTISTS AND DENTAL OFFICE MANAGERS',
  title: 'YOUR DENTAL OFFICE PAPERWORK, ALREADY WRITTEN.',
  sub: 'Consent forms, chairside guides and patient instructions for 10 core procedures. Print it, add your practice name and use it today.',
  stats: [
    { value: '10', label: 'TREATMENT PACKS' },
    { value: '30', label: 'DOCUMENTS READY' },
    { value: '40', label: 'PAGES' },
    { value: '5', label: 'BONUSES INCLUDED' },
  ],
};

// ── 3. TRUST BADGES ───────────────────────────────────────────
export const trustBadges = [
  { top: 'PDF', bottom: 'DIGITAL FORMAT' },
  { top: 'EMAIL', bottom: 'INSTANT DOWNLOAD' },
  { top: '$27', bottom: 'ONE-TIME PAYMENT' },
  { top: '10 DAYS', bottom: 'MONEY-BACK' },
];

// ── 4. SOUND FAMILIAR ────────────────────────────────────────
export const pains = {
  eyebrow: 'SOUND FAMILIAR?',
  title: 'YOU TRAINED TO TREAT PATIENTS. NOT TO WRITE PAPERWORK.',
  items: [
    {
      title: 'COPY-PASTED CONSENT FORMS',
      text: "Generic documents from the internet that don't explain your treatment in words your patients understand.",
    },
    {
      title: 'THE SAME QUESTIONS, EVERY DAY',
      text: 'Patients call after every extraction because nobody handed them clear written instructions.',
    },
    {
      title: 'EVERY ASSISTANT DOES IT DIFFERENTLY',
      text: 'Room setup and steps change with each person on your team.',
    },
    { title: 'TRAINING A NEW HIRE TAKES WEEKS', text: 'There is no written routine to hand over, so you explain everything again.' },
    { title: 'SIGNATURES CHASED AFTER THE FACT', text: 'The form gets signed late, or the risks and alternatives were never written down.' },
    { title: 'EVENINGS SPENT ON ADMIN', text: 'After a full day in the chair, the documents still have to be written.' },
  ],
  bridge: "WHAT IF YOUR NEXT PATIENT'S PAPERWORK WAS ALREADY WRITTEN BEFORE THEY SAT DOWN?",
};

// ── 5. SOLUTION ──────────────────────────────────────────────
export const solution = {
  eyebrow: 'MEET DENTAL OFFICE READY™',
  title: 'THE PAPERWORK IS DONE. YOU JUST PRINT IT AND USE IT.',
  text: 'Ready-to-use material for your practice: 10 treatment packs for the procedures you do every week. Each pack has 3 documents: a consent form ready to sign, a chairside guide for your team and plain-language instructions for your patient.',
  steps: [
    { title: 'PRINT IT', text: 'Pick the treatment and print its pack on Letter or A4.' },
    { title: 'ADD YOUR NAME', text: 'Write or stamp your practice name in the header box.' },
    { title: 'USE IT TODAY', text: 'Explain, sign, treat and hand your patient the instructions.' },
  ],
};

// ── 6. PACK SELECTOR ──────────────────────────────────────────
export const selector = {
  eyebrow: 'SEE A PACK',
  title: 'WHICH TREATMENT DO YOU NEED PAPERWORK FOR TOMORROW?',
  sub: 'Pick one. This is what you print for it.',
  more: 'See all 10 packs',
  columns: ['CONSENT FORM', 'CHAIRSIDE GUIDE', 'PATIENT INSTRUCTIONS'],
  items: [
    {
      tab: 'Tooth extraction',
      n: 'PACK 06',
      consent: ['The procedure in plain words', 'Risks: pain, swelling, bleeding, dry socket, infection', 'Alternatives, including no treatment', 'Patient, dentist and witness signatures'],
      guide: ['Before-the-patient-sits checklist', 'Tray setup in order of use', 'Step-by-step chairside workflow', 'Red flags: stop and call the dentist'],
      patient: ['First 24 hours: gauze, no rinsing, no straws, no smoking', 'First hours, first day and first week', 'Dry socket warning', 'Call us if…'],
    },
    {
      tab: 'Root canal',
      n: 'PACK 07',
      consent: ['What a root canal is, in plain words', 'Why the tooth usually needs a crown', 'Alternatives: extraction or no treatment', 'Patient, dentist and witness signatures'],
      guide: ['Tray setup in order of use', 'Step-by-step chairside workflow', 'Tell the patient about the crown up front', 'Book the crown visit before they leave'],
      patient: ['What to expect after the visit', 'Protect the tooth until the final crown', 'Eating, cleaning and comfort tips', 'Call us if…'],
    },
    {
      tab: 'Teeth whitening',
      n: 'PACK 04',
      consent: ['In-office, at-home or both', 'Risks: sensitivity, gum irritation, uneven results', 'Fillings and crowns do not change color', 'Patient, dentist and witness signatures'],
      guide: ['Shade before and after card', 'Tray setup in order of use', 'Step-by-step chairside workflow', 'Never promise a final shade'],
      patient: ['Avoid for 48 hours: coffee, tea, red wine', 'What is normal: mild sensitivity', 'First hours, first day and first week', 'Call us if…'],
    },
    {
      tab: 'Dental implants',
      n: 'PACK 10',
      consent: ['What an implant is, in plain words', 'Risks explained without scary numbers', 'Patient responsibilities, including not smoking', 'Patient, dentist and witness signatures'],
      guide: ['Implant journey: consultation to maintenance', 'Before-surgery checklist', 'Step-by-step chairside workflow', 'Red flags: stop and call the dentist'],
      patient: ['First 24 hours, first week, healing months', 'Implant care for life', 'What is normal while it heals', 'Call us if…'],
    },
  ],
};

// ── 7. FEATURES (in every pack) ───────────────────────────────
export const features = {
  title: 'IN EVERY TREATMENT PACK.',
  sub: 'All 10 packs follow the same 3-document structure, so your team always knows what to print, set up and hand over.',
  items: [
    {
      icon: 'consent',
      title: 'CONSENT FORM READY TO SIGN',
      text: 'What it is, benefits, risks and alternatives in plain words, with signature lines.',
    },
    { icon: 'tray', title: 'TRAY SETUP DIAGRAM', text: 'Instruments in order of use, so every assistant sets up the room the same way.' },
    { icon: 'steps', title: 'STEP-BY-STEP CHAIRSIDE GUIDE', text: 'From seating the patient to room turnover.' },
    { icon: 'flag', title: 'RED FLAG CARDS', text: 'When to stop and call the dentist, written for the team.' },
    { icon: 'patient', title: 'PATIENT INSTRUCTIONS', text: "Before and after care, what's normal and when to call you." },
    { icon: 'print', title: 'PRINT-READY ON LETTER OR A4', text: 'With a header box for your practice name and logo.' },
  ],
} as const;

// ── 8. THE 10 PACKS, GROUPED ──────────────────────────────────
export const packs = {
  eyebrow: 'READY-TO-USE MATERIAL FOR YOUR PRACTICE',
  title: '10 PACKS FOR THE TREATMENTS YOU DO EVERY WEEK.',
  sub: 'Each one: consent form + chairside guide + patient instructions.',
  groups: [
    {
      label: 'EVERYDAY CARE',
      items: [
        { n: '01', title: 'Dental Cleaning & Exam' },
        { n: '02', title: 'Tooth-Colored Fillings' },
        { n: '03', title: 'Pediatric Sealants & Fluoride' },
      ],
    },
    {
      label: 'COSMETIC & GUM CARE',
      items: [
        { n: '04', title: 'Teeth Whitening' },
        { n: '05', title: 'Scaling & Root Planing' },
      ],
    },
    {
      label: 'TREAT & SAVE',
      items: [
        { n: '06', title: 'Tooth Extraction' },
        { n: '07', title: 'Root Canal Treatment' },
        { n: '08', title: 'Crowns & Bridges' },
      ],
    },
    {
      label: 'ADVANCED',
      items: [
        { n: '09', title: 'Clear Aligner Treatment' },
        { n: '10', title: 'Dental Implants' },
      ],
    },
  ],
};

// ── 9. LOOK INSIDE ───────────────────────────────────────────
export const lookInside = {
  eyebrow: 'A CLOSER LOOK',
  title: 'SEE THE PAGES BEFORE YOU BUY.',
  hint: 'Real pages from the guide · Tap any page to view full size',
  captions: [
    'Cover',
    'The Dental Office Ready system',
    'Consent form ready to sign',
    'Chairside guide',
    'Patient instructions',
    'Office field card',
  ],
};

// ── 10. BONUSES (same order as the checkout) ─────────────────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 PACK',
  title: 'THE PAPERWORK AROUND THE CHAIR. ALREADY WRITTEN.',
  items: [
    {
      label: 'BONUS 01',
      hook: 'Every new patient, covered.',
      title: 'PATIENT INTAKE & FRONT DESK FORMS',
      text: 'New patient registration, health history, medical update, financial policy, cancellation and no-show policy, photo consent, treatment plan acceptance and consent for minors.',
    },
    {
      label: 'BONUS 02',
      hook: 'The same routine, every room.',
      title: 'INFECTION CONTROL & STERILIZATION SOP BINDER',
      text: 'Hand hygiene, PPE, instrument sterilization, surface disinfection, sharps and exposure steps, with daily, weekly and monthly checklists.',
    },
    {
      label: 'BONUS 03',
      hook: 'Ready the day an inspector walks in.',
      title: 'INSPECTION-READY OFFICE BINDER',
      text: 'Opening and closing checklists, sterilizer and spore test logs, emergency kit checks, waste plan, maintenance log and incident report.',
    },
    {
      label: 'BONUS 04',
      hook: 'Know what to say on the phone.',
      title: 'FRONT DESK SCRIPTS — CALLS, TREATMENT PLANS & FOLLOW-UPS',
      text: 'Word-for-word scripts for new patient calls, presenting a treatment plan, answering "it\'s too expensive", confirmations, no-shows and review requests.',
    },
    {
      label: 'BONUS 05',
      hook: 'Train a new assistant without stopping your schedule.',
      title: 'NEW DENTAL ASSISTANT ONBOARDING MANUAL',
      text: 'A 30-day plan, tray setups, a basic instrument guide, room turnover routine and a weekly skills checklist.',
    },
  ],
};

// ── 11. OFFER ─────────────────────────────────────────────────
export const offer = {
  title: 'YOUR OFFICE PAPERWORK, READY TO USE.',
  cardTitle: 'DENTAL OFFICE READY™ · 10 TREATMENT PACKS + ALL 5 BONUSES',
  includedTitle: "WHAT'S INCLUDED",
  included: [
    '10 complete treatment packs',
    'A consent form ready to sign for each',
    'Chairside guide with tray setup',
    'Patient before & after instructions',
    'Red flag cards for the team',
    'Consent builder for any other treatment',
  ],
  bonusesTitle: 'THE 5 BONUSES',
  priceNote: 'USD · ONE-TIME PAYMENT · NO SUBSCRIPTION',
  /** The ONLY button that goes to checkout */
  cta: 'GET DENTAL OFFICE READY™ NOW — $27',
  microcopy: '10-day money-back guarantee · No questions asked',
  delivery: ['Digital PDF · nothing shipped', 'Print on Letter or A4', 'Available worldwide'],
};

// ── 12. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Review the pack and decide if it fits your office. If it isn't right for you, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 13. FAQ ───────────────────────────────────────────────────
export const faq = {
  eyebrow: 'QUESTIONS',
  title: 'BEFORE YOU DECIDE.',
  items: [
    {
      q: 'What exactly do I get for $27?',
      a: 'A 40-page PDF with 10 ready-to-use treatment packs (a consent form, a chairside guide and patient instructions for each procedure), plus the 5 bonuses. One-time payment of $27 USD.',
    },
    {
      q: 'Which procedures are included?',
      a: 'Dental cleaning and exam, tooth-colored fillings, pediatric sealants and fluoride, teeth whitening, scaling and root planing, tooth extraction, root canal treatment, crowns and bridges, clear aligner treatment and dental implants.',
    },
    {
      q: 'Can I add my practice name and logo?',
      a: 'Yes. Every form has a header box for your practice name and logo. Write it, stamp it or add it to your print.',
    },
    {
      q: 'Are the documents editable?',
      a: 'They are print-ready PDFs with fill-in lines. You can print them as they are, or copy the wording into your own templates.',
    },
    {
      q: 'Are the forms valid in my state or country?',
      a: 'They are professional templates written in plain language. Rules change by location, so review them with your local regulations and your professional advisor before use.',
    },
    {
      q: 'Who is it for?',
      a: 'Dentists, practice owners, office managers, front desk coordinators, dental assistants and hygienists.',
    },
    { q: 'Is this a printed book?', a: "No. It's a digital PDF. Nothing is shipped." },
    { q: 'How do I receive it?', a: 'Your download links are emailed right after your payment is approved.' },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 14. FINAL CHOICE ─────────────────────────────────────────
export const finalChoice = {
  title: 'YOUR NEXT PATIENT: TWO WAYS TO START.',
  without: {
    title: 'WITHOUT A SYSTEM',
    items: [
      'Another evening rewriting consent forms',
      'Generic documents copied from the internet',
      'Patients calling with the same questions',
      'Every assistant setting up differently',
      'Signatures chased after the treatment',
    ],
  },
  with: {
    title: 'WITH DENTAL OFFICE READY™',
    items: [
      'Consent forms ready to sign',
      'The same chairside steps every time',
      "Clear instructions in every patient's hands",
      'A written routine for every new hire',
      'An organized, professional office',
    ],
  },
  microcopy: '10 treatment packs + 5 bonuses · $27 USD, one-time payment',
};

// ── 15. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'DENTAL OFFICE READY™ · Digital resource for dental offices',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
